import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RemoteControlSessionService } from './session.service';
import { McRcEventsService, AnnotationDrawingMode, AnnotationOperatingMode, IRcAppUI } from '../../remote-control-ng';
import { AnnotationState, AnnotationTool, DrawingMode, OperatingMode } from './remote-control.interfaces';
import { hex2rgb, rgb2hex } from './color';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { LicenceInfoService } from '@nx-fe/services';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import * as annotationMsg from '../../remote-control-ng/messages/pocket-com';

// FIXME [mkotormus]: introducing mapping function since AnnotationState uses strings
// and we would not like to have legacy UI dependency on the MCRC module in Angular 6 UI.
// Once legacy UI is removed, can use mcrc enums in AnnotationState directly and get rid
// of these conversions
function toMcRcDrawingMode(mode: DrawingMode): AnnotationDrawingMode {
    switch (mode) {
        case 'adminonly': return AnnotationDrawingMode.AdminOnly;
        case 'allusers': return AnnotationDrawingMode.BothUsers;
    }
}

function toMcRcOperatingMode(mode: OperatingMode): AnnotationOperatingMode | undefined{
    switch (mode) {
        case 'annotation': return AnnotationOperatingMode.Annotation;
        case 'whiteboard': return AnnotationOperatingMode.Whiteboard;
    }
}

export interface AnnotationStatus {
    // PocketComm Win32 error
    error?: number;
}

/**
 * RemoteControl Annotation Service:
 *  - Abstracts away communication with the agent via mcrc
 *  - Mintains annotation state
 */
@Injectable()
export class RemoteControlAnnotationsService implements OnDestroy {

    public readonly annotationAvailableAgentVersion = '14.0.0';
    public readonly annotationAvailableOsVersion = '5.1.0';

    public readonly colorPalette = [
        '#f1614f',
        '#32b6e9',
        '#aed255',
        '#fbad1c',
        '#7c7c81',
        '#ffffff',
        '#000000'
    ];

    private readonly annotationStatusChanged: BroadcastService<AnnotationStatus> = new BroadcastService(BroadcastChannels.onAnnotationStatusChanged);

    public get isAnnotationSupported(): boolean {
        const isCompatibleAgentVersion = this.rcService.compareAgentVersion(this.annotationAvailableAgentVersion) >= 0;
        const iscompatibleOs = this.rcService.compareOsVersion(this.annotationAvailableOsVersion) >= 0;
        return this.rcService.isAndroidDevice && isCompatibleAgentVersion && iscompatibleOs;
    }

    public get isAnnotationEnabled(): boolean {
        return  this.isAnnotationSupported && this.licenseService.isLicenseActive();
    }

    public set currentState(value: AnnotationState) {
        const rcState = this.rcService.rcState;
        if (!rcState) { return; }
        rcState.annotations = value;
    }

    public get currentState(): AnnotationState {
        const rcState = this.rcService.rcState;
        if (!rcState) { return this._cloneFromDefaultState({}); }
        if (!rcState.annotations) { rcState.annotations = this._cloneFromDefaultState({}); }
        return rcState.annotations;
    }

    public get onAnnotationStart$(): Observable<AnnotationState> {
        return this.onAnnotationStart.pipe(takeUntil(this.onDestroy));
    }

    public get onAnnotationEnd$(): Observable<AnnotationState> {
        return this.onAnnotationEnd.pipe(takeUntil(this.onDestroy));
    }

    private onDestroy = new Subject<void>();
    private onAnnotationStart = new Subject<AnnotationState>();
    private onAnnotationEnd = new Subject<AnnotationState>();

    constructor(
        private mcrcEventsService: McRcEventsService,
        private licenseService: LicenceInfoService,
        public rcService: RemoteControlSessionService
    ) {
        // Initialize default state as early as possible (need for unit tests)
        if (rcService.rcState) {
            rcService.rcState.annotations = this._cloneFromDefaultState({});
        }

        this.mcrcEventsService.rcAnnotationMessage
            .pipe(takeUntil(this.onDestroy))
            .subscribe(this.onDeviceAnnotationMessage);
    }

    private get appUI(): IRcAppUI {
        return RcContext.instance.session.appUI;
    }

    public ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();
    }

    /**
     * Handles messages which were initiated by the device user.
     * For now just handles pausing/resuming annotations as device
     * user interacts with the floating button.
     */
    public onDeviceAnnotationMessage = (event: any) => {
        if (event) {
            // Since this command arrives from device
            // we do not need to send command down to it
            // as is done in pause/resumeAnnotations
            // therefore here we just update the state
            const nextState = this._cloneFromCurrentState({
                annotationPaused: !event.toggleState
            });
            this._syncCursorState(nextState);
            this.currentState = nextState;
        }
    };

    public async setOperatingMode(mode: OperatingMode): Promise<void> {
        const nextState = this._cloneFromCurrentState({
            operatingMode: mode,
            // Enforces allusers for whiteboard mode and sets to default of adminonly in annotation mode
            drawingMode: mode === 'whiteboard' ? 'allusers' : 'adminonly'
        });
        await new Promise((resolve, reject) => {
            RcContext.instance.session.sendRequest(
                new annotationMsg.AnnotationStartAnnotationMessage(
                    toMcRcOperatingMode(nextState.operatingMode),
                    toMcRcDrawingMode(nextState.drawingMode)))
                .pipe(takeUntil(this.onDestroy))
                .subscribe(() => resolve(undefined));
        });
        this.currentState = nextState;
    }

    public async setDrawingMode(mode: DrawingMode): Promise<void> {
        // Enforce all users when in whiteboard mode
        const nextState = this._cloneFromCurrentState({
            drawingMode: this.currentState.operatingMode !== 'whiteboard' ? mode : 'allusers'
        });
        await new Promise((resolve, reject) => {
            RcContext.instance.session.sendRequest(
                new annotationMsg.AnnotationStartAnnotationMessage(
                    toMcRcOperatingMode(nextState.operatingMode),
                    toMcRcDrawingMode(nextState.drawingMode)))
                .pipe(takeUntil(this.onDestroy))
                .subscribe(() => resolve(undefined));
        });
        this.currentState = nextState;
    }

    public async resetAnnotations(clearOnly: boolean): Promise<void> {
        try {
            // Suspend mouse input immediately, until reset is complete
            this.appUI.disableMouseInput(true);
            // Prepare state after reset: transfer properties which should not be reset
            // in case of reset command or keep all settings as before in case of clear only
            const nextState = clearOnly ? this._cloneFromCurrentState() : this._cloneFromDefaultState({
                isAnnotating: true,
                operatingMode: this.currentState.operatingMode,
                drawingMode: this.currentState.drawingMode
            });
            await new Promise((resolve, reject) => {
                if (clearOnly) {
                    RcContext.instance.session.sendRequest(
                        new annotationMsg.AnnotationClearMessage())
                        .pipe(takeUntil(this.onDestroy))
                        .subscribe(() => resolve(undefined));
                } else {
                    RcContext.instance.session.sendRequest(
                        new annotationMsg.AnnotationResetMessage())
                        .pipe(takeUntil(this.onDestroy))
                        .subscribe(() => resolve(undefined));
                }
            });
            this._syncCursorState(nextState);
            await this._syncActiveAnnotationToolState(nextState);
            this.currentState = nextState;
        } finally {
            this.appUI.disableMouseInput(false);
        }
    }

    public async startAnnotations(): Promise<void> {
        try {
            if (!(this.currentState.isAnnotating || this.currentState.isPendingAccept)) {
                this.appUI.disableMouseInput(true);
                this.currentState = this._cloneFromDefaultState({ isPendingAccept: true });
                const nextState = this._cloneFromDefaultState({ isAnnotating: true, isPendingAccept: false });
                await new Promise((resolve, reject) => {
                    RcContext.instance.session.sendRequest(
                        new annotationMsg.AnnotationStartAnnotationMessage(
                            toMcRcOperatingMode(nextState.operatingMode),
                            toMcRcDrawingMode(nextState.drawingMode)))
                        .pipe(takeUntil(this.onDestroy))
                        .subscribe((message: annotationMsg.AnnotationMessage) => {
                            if (message.response?.errorCode) {
                                reject(message.response.errorCode);
                            }
                            resolve(undefined);
                        });
                });
                await this._syncActiveAnnotationToolState(nextState);
                this._syncCursorState(nextState);
                this.currentState = nextState;
                this.onAnnotationStart.next(this.currentState);
            } else {
                console.warn('startAnnotations request rejected. Annotation session is already in progress.');
            }
        } catch (error) {
            this.currentState = this._cloneFromDefaultState({ isPendingAccept: false });
            this.annotationStatusChanged.postMessage({ error });
        } finally {
            this.appUI.disableMouseInput(false);
        }
    }

    public async stopAnnotations(): Promise<void> {
        if (this.currentState.isAnnotating) {
            // In case mouse input was suspsended, always re-enable
            // when exiting annotations.
            this.appUI.disableMouseInput(false);
            const nextState = this._cloneFromCurrentState({
                isAnnotating: false
            });
            await new Promise((resolve, reject) => {
                RcContext.instance.session.sendRequest(
                    new annotationMsg.AnnotationStopMessage())
                    .pipe(takeUntil(this.onDestroy))
                    .subscribe(() => resolve(undefined));
            });
            this._syncCursorState(nextState);
            this.currentState = nextState;
            this.onAnnotationEnd.next(this.currentState);
        } else {
            console.warn('stopAnnotations request rejected. Annotation session is not in progress.');
        }
    }

    public async pauseAnnotations(): Promise<void> {
        if (!this.currentState.annotationPaused && this.currentState.isAnnotating) {
            const nextState = this._cloneFromCurrentState({
                annotationPaused: true
            });
            await new Promise((resolve, reject) => {
                RcContext.instance.session.sendRequest(
                    new annotationMsg.AnnotationToggleMessage(1))
                    .pipe(takeUntil(this.onDestroy))
                    .subscribe(() => resolve(undefined));
            });
            this._syncCursorState(nextState);
            this.currentState = nextState;
        } else {
            console.warn('pauseAnnotations request rejected. Annotation session is not in progress or is already paused.');
        }
    }

    public async resumeAnnotations(): Promise<void> {
        if (this.currentState.isAnnotating && this.currentState.annotationPaused) {
            const nextState = this._cloneFromCurrentState({
                annotationPaused: false
            });
            await new Promise((resolve, reject) => {
                // this.mcrc.utils.messages.toggleAnnotation(false, () => resolve(undefined));
                RcContext.instance.session.sendRequest(
                    new annotationMsg.AnnotationToggleMessage(0))
                    .pipe(takeUntil(this.onDestroy))
                    .subscribe(() => resolve(undefined));
            });
            this._syncCursorState(nextState);
            await this._syncActiveAnnotationToolState(nextState);
            this.currentState = nextState;
        }  else {
            console.warn('resumeAnnotations request rejected. Annotation session is not running or is not paused.');
        }
    }

    public async setAnnotationTool(tool: AnnotationTool): Promise<void> {
        const nextState = this._cloneFromCurrentState({
            annotationTool: tool
        });
        this.appUI.setAnnotationTool(tool);
        await this._syncActiveAnnotationToolState(nextState);
        this.currentState = nextState;
    }

    public async setStrokeWidth(width: number): Promise<void> {
        const nextState = this._cloneFromCurrentState({
            strokeWidth: width
        });
        this._syncCursorState(nextState);
        await this._syncActiveAnnotationToolState(nextState);
        this.currentState = nextState;
    }

    public async setStrokeColor(color: string): Promise<void> {
        const nextState = this._cloneFromCurrentState({
            strokeColor: hex2rgb(color)
        });
        this._syncCursorState(nextState);
        await this._syncActiveAnnotationToolState(nextState);
        this.currentState = nextState;
    }

    private _syncCursorState(state: AnnotationState) {
        if (state.isAnnotating && !state.annotationPaused) {
            this.appUI.setAnnotationTool(state.annotationTool as any);
            this.appUI.setAnnotationCursorColor(rgb2hex(state.strokeColor));
            this.appUI.setAnnotationCursorSize(state.strokeWidth);
            this.appUI.showAnnotationsToolHint(true);
        } else {
            this.appUI.showAnnotationsToolHint(false);
        }
    }

    // Attempts to assign new annotation tool configuration to the device
    // based on the passed annotation state configuration and depending on
    // if the tool is valid and assignment was successsfull will resolve or
    // reject the promise
    private async _syncActiveAnnotationToolState(state: AnnotationState): Promise<void> {
        if (state.isAnnotating) {
            await new Promise((resolve, reject) => {
                switch (state.annotationTool) {
                    case 'pen':
                        RcContext.instance.session.sendRequest(
                            new annotationMsg.AnnotationSetPenMessage(rgb2hex(state.strokeColor), state.strokeWidth))
                            .pipe(takeUntil(this.onDestroy))
                            .subscribe(() => resolve(undefined));
                        break;
                    case 'eraser':
                        RcContext.instance.session.sendRequest(
                            new annotationMsg.AnnotationEraserMessage(state.strokeWidth))
                            .pipe(takeUntil(this.onDestroy))
                            .subscribe(() => resolve(undefined));
                        break;
                    case 'laser':
                        RcContext.instance.session.sendRequest(
                            new annotationMsg.AnnotationSetLaserModeMessage())
                            .pipe(takeUntil(this.onDestroy))
                            .subscribe(() => resolve(undefined));
                        break;
                    default: reject(new Error('Invalid annotation tool'));
                }
            });
        }
    }

    private _cloneFromDefaultState(state: Partial<AnnotationState>) {
        return <AnnotationState> {
            drawingMode: state.drawingMode || 'adminonly',
            operatingMode: state.operatingMode || 'annotation',
            annotationPaused: state.annotationPaused || false,
            annotationTool: state.annotationTool || 'pen',
            isAnnotating: state.isAnnotating || false,
            isPendingAccept: state.isPendingAccept || false,
            strokeColor: state.strokeColor || hex2rgb(this.colorPalette[0]),
            strokeWidth: state.strokeWidth || 10
        };
    }

    private _cloneFromCurrentState = (state?: Partial<AnnotationState>) => Object.assign(Object.assign({}, this.currentState), state);
}
